import { useEffect, useState } from 'react';
import { Divider } from 'antd';
import { useAtomValue } from 'jotai/index';
import { ModulesMenu } from 'components/atoms/ModulesMenu/ModulesMenu';
import { AccountManageMenu } from 'components/atoms/AccountManageMenu/AccountManageMenu';
import { userDetailsAtom } from 'jotaiAtoms/userDetails';
import { useCurrentChild } from 'jotaiAtoms/currentChild';
import { AppText, NavigationChildInstitutionInfo } from 'components/atoms';
import { useIsMobile } from 'hooks/useIsMobile/useIsMobile';
import { IconAlertTriangle } from '@tabler/icons-react';
import styled, { css } from 'styled-components';
import { getSideMenuNavigationConfig } from './sideMenuNavigationConfig';
import { commonTexts } from '../../../../../consts/text';
import { colors } from '../../../../../theme/styledComponents/colors';

type SideMenuNavigationProps = {
    collapsed: boolean;
};

const Container = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: space-between;
`;

const StyledAppText = styled(AppText)`
    ${({ theme }) => css`
        text-align: center;
        color: ${theme.colors.orange.orange1};
    `};
`;

export const SideMenuNavigation = ({ collapsed }: SideMenuNavigationProps) => {
    const userDetails = useAtomValue(userDetailsAtom);
    const { institutionAddress: childInstitutionAddress } = useCurrentChild();

    const [isVisibleChildInstitutionInfo, setIsVisibleChildInstitutionInfo] = useState(false);

    const isMobile = useIsMobile();

    useEffect(() => {
        const isVisibleChildInstitutionInfoValue =
            (!collapsed || isMobile) && childInstitutionAddress.institutionId;
        if (!isVisibleChildInstitutionInfoValue) {
            setIsVisibleChildInstitutionInfo(false);
            return undefined;
        }
        const timeoutId = setTimeout(() => {
            setIsVisibleChildInstitutionInfo(true);
        }, 100);
        return () => clearTimeout(timeoutId);
    }, [childInstitutionAddress.institutionId, collapsed, isMobile]);

    const sideMenuNavigationConfig = userDetails?.type
        ? getSideMenuNavigationConfig(userDetails.type)
        : null;

    const isInstitutionVerified = userDetails?.institutionVerified ?? true;

    if (!sideMenuNavigationConfig) {
        return null;
    }

    const { Dropdown, modulesButtonsData, accountManageItems } = sideMenuNavigationConfig;

    return (
        <>
            {Dropdown}
            {isInstitutionVerified ? (
                <>
                    <Divider />
                    <ModulesMenu moduleButtonsData={modulesButtonsData} />
                </>
            ) : (
                <>
                    <Divider />
                    <Container>
                        <IconAlertTriangle size={32} color={colors.orange.orange1} />
                        {(!collapsed || isMobile) && (
                            <StyledAppText margin="marginNormal">
                                {commonTexts.warningMessages.unverifiedInstitution}
                            </StyledAppText>
                        )}
                    </Container>
                </>
            )}
            <Divider />
            <AccountManageMenu menuItems={accountManageItems} />
            {isVisibleChildInstitutionInfo && (
                <NavigationChildInstitutionInfo institutionData={childInstitutionAddress} />
            )}
        </>
    );
};
